import {_F} from "../utility/func";
import type {SoilQuery} from "./soil-query-service";
import {L} from "./landcare-toolkit";
import {Coordinate} from "ol/coordinate";
import FactsheetType = _F.FactsheetType;
import b64EncodeUnicode = _F.b64EncodeUnicode;


export function getSmapFactSheetLink(soilname, rank, mapunitsymbol, gis, x, y, confidence, region, factsheetType: FactsheetType='smap') : string {
    let u = encodeURIComponent;
    let license = L.util.Layer.getLicense('factsheets');
    let licenseEncoded = b64EncodeUnicode(license && license['Value'] || '');
    return `${window['LTConfiguration'].factsheetsAPI}reports?access=spatial&type=${factsheetType}&gis=`
        + u(gis.trim()) + '&rank=' + u(rank) + '&mapunitsymbol=' + u(mapunitsymbol)
        + '&confidence=' + u(confidence) + '&xcoord=' + u(x) + '&ycoord=' + u(y)
        + '&region=' + u(region) + '&licence=' + u(licenseEncoded);
}
export interface ISoilQueryPartial {
    soilName:string,
    proportion:string|number,
    description:string,
    confidence:string,
    confidenceClass?: string[];
    index?: number,
    factSheetURL?: string
    improvedFactSheetURL?: string
}
export interface ISMU {
    gislayer: string;
    soilmapuc: string;
}

export type FactsheetTypes = "smu" | "teReoSibling";

export interface ISMUWithFactsheetType extends ISMU {
    factsheetType?: FactsheetTypes;
    siblingNumber?: number;
    [key: string]: any;
}

const confindences = {
    'L' : 'Low',
    'M' : 'Medium',
    'H' : 'High'
};

const confidenceClasses = {
    'L' : 'confidence-low',
    'M' : 'confidence-medium',
    'H' : 'confidence-high',
};

export const BARE_ROCK = "Bare rock";

export class SoilInformation {
    constructor(public coord : Coordinate,
                public data : SoilQuery.IPointQueryData,
                protected _id : string|number = '') {
    }

    isSmap() : boolean {
        return this.data && this.data.layers && (this.data.layers.length > 0)
            && (this.data.layers[0].rows.length > 0);
    }

    isSeaOrRiver(): boolean {
        return this.data && (["sea", "river"].indexOf(this.data.poiType) > -1);
    }

    isFeature(): boolean {
        return !this.isSmap() && !this.isSeaOrRiver() && !this.isNotSmap() && !this.isECAN();
    }

    isNotSmap(): boolean {
        return this.data && this.data.poiType == "NotSmap";
    }

    isECAN(): boolean {
        return this.data && this.data.poiType && this.data.poiType.indexOf("ECAN") == 0;
    }

    soilViewLink(layerName : string) {
        let x = this.coord[0];
        let y = this.coord[1];
        let bboxL = x - 1500;
        let bboxR = x + 1500;
        let bboxT = y - 1500;
        let bboxB = y + 1500;
        return `https://soils-maps.landcareresearch.co.nz/?layername=${layerName}&idcolumn=&idvalue=&mapfile=fsl&srs=EPSG:2193&Mode=QUERY&`
            + `BBOX=${bboxL},${bboxT},${bboxR},${bboxB}&X=${x}&Y=${y}`;
    }

    getECANLink(): string {
        let x = this.coord[0];
        let y = this.coord[1];
        let bboxL = x - 1500;
        let bboxR = x + 1500;
        let bboxT = y - 1500;
        let bboxB = y + 1500;
        return "https://apps.canterburymaps.govt.nz/lrisupport/?" +
            `&x=${x}&y=${y}&bbox=${bboxL},${bboxB},${bboxR},${bboxT}`
    }

    featureType(): string {
        return this.data.poiType;
    }

    fslSoilDrainageLink(): string {
        return this.soilViewLink('fsl_drain')
    }

    fslSoilOrderLink(): string {
        return this.soilViewLink('fsl_nzsc');
    }



    getId() : string|number { return this._id; };

    protected _rows: ISoilQueryPartial[] = null;

    friendlyCoords() : String {
        return Math.round(this.coord[0]) + " " + Math.round(this.coord[1]);
    }

    get columns(): SoilQuery.ILayerPointQueryColumn[] {
        const layers = this.data && this.data.layers || [];
        const firstLayer = layers.length && layers[0]
        const firstRow = firstLayer && firstLayer.rows && firstLayer.rows.length && firstLayer.rows[0];
        return firstRow && firstRow.columns || [];
    }

    smu(): ISMU {
        const columns = this.columns;

        if (!columns.length) {
            return null;
        }

        const gislayerCol =  columns.find(cl => cl.name === 'gislayer');
        const soilmapucCol = columns.find(cl => cl.name === 'soilmapuc');

        if (!gislayerCol || !soilmapucCol) return null;

        return {
            gislayer: (gislayerCol && gislayerCol.value) + '',
            soilmapuc: (soilmapucCol && soilmapucCol.value) + ''
        }
    }

    objectId(): string|number {
        return this.columns.filter(c => c.name === 'objectid').map(c => c.value)[0];
    }

    rows() : ISoilQueryPartial[] {
        if (this._rows !== null) {
            return this._rows;
        }
        if (!this.data.layers.length)return [];
        let region = this.data['region'];

        let firstLayer = this.data.layers[0];
        if (!firstLayer.rows.length) return [];
        let rows = firstLayer.rows;
        if (!rows.length) return [];

        let columns = rows[0].columns;

        let hash: { [key:string]:string|number }= {};

        columns.map(col => {
            hash[col.name] = col.value;
        });

        let results: ISoilQueryPartial[] = [];



        for (let i=1; i<=5; i++) {
            if (hash['soilname' + i]) {
                let confidenceCode = (hash['confidence' + i] || "").toString();
                let confidence = (confidenceCode in confindences) ? confindences[confidenceCode] : "";

                let confidenceClass = ["stats-confidence"];
                if (confidenceCode in confidenceClasses) confidenceClass.push(confidenceClasses[confidenceCode]);

                let soilname = hash['soilname' + i];

                const regionCode = region.replace(' Region', '').replace("'", '');

                let factSheetURL = soilname != BARE_ROCK ?  getSmapFactSheetLink(
                    soilname,
                    i,
                    hash['soilmapuc'],
                    hash['gislayer'],
                    this.coord[0],
                    this.coord[1],
                    hash['confidence' + i],
                    regionCode
                ) : "";

                const improvedFactSheetURL = soilname != BARE_ROCK ?  getSmapFactSheetLink(
                    soilname,
                    i,
                    hash['soilmapuc'],
                    hash['gislayer'],
                    this.coord[0],
                    this.coord[1],
                    hash['confidence' + i],
                    regionCode,
                    'smap-v2'
                ) : "";

                const siblingData: ISoilQueryPartial = {
                    soilName : (soilname || "").toString(),
                    proportion: hash['proportion' + i] || NaN,
                    description: (hash['description' + i] || "").toString(),
                    confidence : confidence,
                    confidenceClass : confidenceClass,
                    factSheetURL,
                    improvedFactSheetURL,
                    index: i
                };
                results.push(<ISoilQueryPartial> siblingData);
            }
        }
        this._rows = results;
        return results;
    }

    public toJSON() : {} {
        return {
            coord:  this.coord,
            data:   this.data,
            id:     this._id
        };
    }
}


export interface IEnvironmentalSusceptibilityRow {
    soilName: string;
    proportion: string|number;
    confidence: string;
    confidenceClass: string;
    relativebypassflow: string;
    relativenleachrisk: string;
}

export class EnvironmentalSusceptibility {
    public rows: IEnvironmentalSusceptibilityRow[] = [];

    constructor(public data : SoilQuery.IPointQueryData,
                protected _id : string|number = '') {
        this.rows = this.populateRows();
    }

    protected populateRows(): IEnvironmentalSusceptibilityRow[] {
        const firstLayer = this.data.layers[0];
        if (!firstLayer.rows.length) return [];

        let rows = firstLayer.rows;
        if (!rows.length) return [];

        let columns = rows[0].columns;
        if (!columns.length) return [];

        let hash: { [key:string]:string|number }= {};

        columns.map(col => {
            hash[col.name] = col.value;
        });

        const results: IEnvironmentalSusceptibilityRow[] = [];

        for (let i=1; i<=5; i++) {
            if (hash['soilname' + i]) {
                let confidenceCode = (hash['confidence' + i] || "").toString();
                let confidence = (confidenceCode in confindences) ? confindences[confidenceCode] : "";

                let confidenceClass = ["stats-confidence"];
                if (confidenceCode in confidenceClasses) confidenceClass.push(confidenceClasses[confidenceCode]);

                results.push({
                    confidence,
                    confidenceClass: confidenceClass.join(' '),
                    proportion: hash['proportion' + i] as any,
                    relativebypassflow: hash['relativebypassflowsib' + i] as any,
                    relativenleachrisk: hash['relativenleachrisksib' + i] as any,
                    soilName: hash['soilname' + i] as any
                });
            }
        }

        return results;
    }
}
